// Saved report definitions client (reporting foundation R3) — talks to the
// payroll-service definitions API via the gateway. Plain employees (no reports
// role) get a 403 on every call; callers should treat that as "hide the
// feature", not an error.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1/reports/definitions';

// ---- Types --------------------------------------------------------------------

/** Server-side report domains (must match the payroll-service enum). */
export type ReportDomain =
  | 'performance'
  | 'learning'
  | 'employee'
  | 'payroll'
  | 'payroll_readiness'
  | 'benefit_contributions'
  | 'work_assignments'
  | 'work_items'
  | 'work_activity'
  | 'work_today'
  | 'employees_no_work'
  | 'work_reconciliation'
  | 'work_conflicts'
  | 'statutory_compliance'
  | 'attendance'
  | 'overtime'
  | 'leave'
  | 'loans_advances'
  | 'documents'
  | 'risk_fraud'
  | 'branch_project_labor_cost'
  | 'audit';

export const DOMAIN_LABELS: Record<ReportDomain, string> = {
  performance: 'Performance',
  learning: 'Learning & Development',
  employee: 'Employee',
  payroll: 'Payroll',
  payroll_readiness: 'Payroll readiness',
  benefit_contributions: 'Benefit contributions',
  work_assignments: 'Work assignments',
  work_items: 'Work items & reviews',
  work_activity: 'Workforce work activity',
  work_today: "Who's working today",
  employees_no_work: 'Employees without active work',
  work_reconciliation: 'Assignment ↔ work reconciliation',
  work_conflicts: 'Scheduling conflicts',
  statutory_compliance: 'Statutory & compliance',
  attendance: 'Attendance',
  overtime: 'Overtime',
  leave: 'Leave',
  loans_advances: 'Loans & advances',
  documents: 'Documents',
  risk_fraud: 'Risk & fraud',
  branch_project_labor_cost: 'Branch / project labor cost',
  audit: 'Audit',
};

export type SavedReportVisibility = 'private' | 'role' | 'users' | 'tenant';
export type SavedReportStatus = 'active' | 'draft' | 'archived';

/** Filter values a saved definition rehydrates into the viewer. */
export interface SavedReportFilters {
  runId?: string;
  from?: string;
  to?: string;
  status?: string;
  [key: string]: unknown;
}

export interface SavedReport {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  domain: ReportDomain;
  template_id: string | null;
  columns: string[];
  filters: SavedReportFilters;
  group_by: string[] | null;
  /** R6 encoding: 'count' | 'sum:<field>' | 'avg:<field>' | 'min:<field>' | 'max:<field>'. */
  aggregations: string[] | null;
  sort: Record<string, unknown> | null;
  date_range: Record<string, unknown> | null;
  visibility: SavedReportVisibility;
  shared_roles: string[] | null;
  shared_users: string[] | null;
  export_formats: string[] | null;
  owner_user_id: string;
  status: SavedReportStatus;
  last_run_at: string | null;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export interface SavedReportInput {
  name: string;
  description?: string;
  domain: ReportDomain;
  template_id?: string;
  columns?: string[];
  filters?: SavedReportFilters;
  /** R6: keys of groupable catalog fields. */
  group_by?: string[];
  /** R6 encoding: 'count' | 'sum:<field>' | 'avg:<field>' | 'min:<field>' | 'max:<field>'. */
  aggregations?: string[];
  /**
   * 'tenant' stays owner-only (403 otherwise); any non-private visibility
   * needs reports:share. Role shares 400 with code 'share_blocked' when a
   * column is invisible to a recipient role.
   */
  visibility?: SavedReportVisibility;
  /** Canonical role keys: hr_manager, finance, hr_staff, manager. */
  shared_roles?: string[];
  shared_users?: string[];
  status?: SavedReportStatus;
}

// ---- Client ---------------------------------------------------------------------

export const listSavedReports = (
  status: SavedReportStatus | 'all' = 'active',
): Promise<SavedReport[]> =>
  gatewayApi
    .get<{ definitions: SavedReport[] }>(BASE, { params: { status } })
    .then((r) => r.data.definitions ?? []);

export const getSavedReport = (id: string): Promise<SavedReport> =>
  gatewayApi.get<SavedReport>(`${BASE}/${id}`).then((r) => r.data);

export const createSavedReport = (body: SavedReportInput): Promise<SavedReport> =>
  gatewayApi.post<SavedReport>(BASE, body).then((r) => r.data);

export const updateSavedReport = (id: string, body: SavedReportInput): Promise<SavedReport> =>
  gatewayApi.put<SavedReport>(`${BASE}/${id}`, body).then((r) => r.data);

/** Archives (soft-deletes) the definition. */
export const archiveSavedReport = (id: string): Promise<void> =>
  gatewayApi.delete(`${BASE}/${id}`).then(() => undefined);

export const duplicateSavedReport = (id: string): Promise<SavedReport> =>
  gatewayApi.post<SavedReport>(`${BASE}/${id}/duplicate`).then((r) => r.data);

/** Stamp last_run_at — best-effort, never block the viewer on it. */
export const markSavedReportRan = (id: string): Promise<void> =>
  gatewayApi.post(`${BASE}/${id}/ran`).then(() => undefined);
